/**
 * Product Image Loader
 * 
 * Modern implementation for loading product images from templates
 * and handling image cycling for product cards.
 */

class ProductImageLoader {
  constructor() {
    this.loadedProducts = new Map(); // Track loaded products
    this.cyclingProducts = new Map(); // Track products with active cycling
    this.isMobile = window.matchMedia('(max-width: 990px)').matches;
    this.debug = false; // Disable debugging
    
    // Disable memory debugging by default
    this.memoryDebug = false;
    
    this.cleanupEnabled = true; // Enable/disable image cleanup
    
    // Configuration
    this.config = {
      cycleDelay: 1300,      // Delay before starting to cycle images
      cycleInterval: 1800,   // Interval between image changes
      loadTimeout: 3000,     // Timeout for image loading
      debounceDelay: 250,    // Debounce delay for scroll events
      cleanupDelay: 1000,    // Delay before cleaning up images after hover ends
      viewportBuffer: 1000,  // Buffer distance (px) above and below viewport for keeping images
      memoryUpdateInterval: 2000 // Interval for updating memory usage display (ms)
    };
    
    // Initialize
    this.init();
  }
  
  /**
   * Initialize the image loader
   */
  init() {
    // Set up intersection observer for lazy loading
    this.setupIntersectionObserver();
    
    // Bind events based on device type
    if (this.isMobile) {
      this.bindMobileEvents();
    } else {
      this.bindDesktopEvents();
    }
    
    // Handle window resize
    this.handleResize();
    
    // Initialize visible products
    this.initVisibleProducts();
    
    // Set up scroll listener for cleanup
    if (this.cleanupEnabled) {
      this.setupScrollListener();
    }
  }
  
  /**
   * Initialize memory debugger
   */
  initMemoryDebugger() {
    // Create memory debugger element
    const debuggerEl = document.createElement('div');
    debuggerEl.id = 'memory-debugger';
    debuggerEl.style.cssText = `
      position: fixed;
      bottom: 10px;
      right: 10px;
      background: rgba(0, 0, 0, 0.8);
      color: #fff;
      padding: 10px;
      border-radius: 5px;
      font-family: monospace;
      font-size: 12px;
      z-index: 9999;
      max-width: 300px;
      pointer-events: none;
    `;
    document.body.appendChild(debuggerEl);
    
    // Create memory usage element
    const memoryEl = document.createElement('div');
    memoryEl.id = 'memory-usage';
    debuggerEl.appendChild(memoryEl);
    
    // Create DOM stats element
    const domStatsEl = document.createElement('div');
    domStatsEl.id = 'dom-stats';
    debuggerEl.appendChild(domStatsEl);
    
    // Create product stats element
    const productStatsEl = document.createElement('div');
    productStatsEl.id = 'product-stats';
    debuggerEl.appendChild(productStatsEl);
    
    // Create image stats element
    const imageStatsEl = document.createElement('div');
    imageStatsEl.id = 'image-stats';
    debuggerEl.appendChild(imageStatsEl);
    
    // Update memory usage periodically
    this.memoryUpdateInterval = setInterval(() => {
      this.updateMemoryDebugger();
    }, this.config.memoryUpdateInterval);
  }
  
  /**
   * Update memory debugger display
   */
  updateMemoryDebugger() {
    if (!this.memoryDebug) return;
    
    // Update memory usage
    const memoryEl = document.getElementById('memory-usage');
    if (memoryEl && window.performance && window.performance.memory) {
      const memory = window.performance.memory;
      const usedHeapSize = (memory.usedJSHeapSize / 1048576).toFixed(2);
      const totalHeapSize = (memory.totalJSHeapSize / 1048576).toFixed(2);
      const heapLimit = (memory.jsHeapSizeLimit / 1048576).toFixed(2);
      
      memoryEl.innerHTML = `
        <strong>Memory:</strong><br>
        Used: ${usedHeapSize} MB<br>
        Total: ${totalHeapSize} MB<br>
        Limit: ${heapLimit} MB<br>
        Usage: ${(usedHeapSize / heapLimit * 100).toFixed(2)}%
      `;
    } else if (memoryEl) {
      memoryEl.innerHTML = '<strong>Memory:</strong> Not available';
    }
    
    // Update DOM stats
    const domStatsEl = document.getElementById('dom-stats');
    if (domStatsEl) {
      const elementCount = document.getElementsByTagName('*').length;
      const imageCount = document.getElementsByTagName('img').length;
      
      domStatsEl.innerHTML = `
        <strong>DOM:</strong><br>
        Elements: ${elementCount}<br>
        Images: ${imageCount}
      `;
    }
    
    // Update product stats
    const productStatsEl = document.getElementById('product-stats');
    if (productStatsEl) {
      const productCount = document.querySelectorAll('.card-wrapper').length;
      const loadedCount = this.loadedProducts.size;
      const cyclingCount = this.cyclingProducts.size;
      
      productStatsEl.innerHTML = `
        <strong>Products:</strong><br>
        Total: ${productCount}<br>
        Loaded: ${loadedCount}<br>
        Cycling: ${cyclingCount}
      `;
    }
    
    // Update image stats
    const imageStatsEl = document.getElementById('image-stats');
    if (imageStatsEl) {
      let totalTemplateImages = 0;
      let loadedTemplateImages = 0;
      
      document.querySelectorAll('.product-images-template').forEach(template => {
        if (template.content) {
          totalTemplateImages += template.content.querySelectorAll('img').length;
        }
      });
      
      document.querySelectorAll('.card-wrapper').forEach(card => {
        loadedTemplateImages += card.querySelectorAll('.product-desktop-image').length - 1; // Subtract primary image
      });
      
      imageStatsEl.innerHTML = `
        <strong>Template Images:</strong><br>
        Total: ${totalTemplateImages}<br>
        Loaded: ${loadedTemplateImages}<br>
        Ratio: ${(loadedTemplateImages / totalTemplateImages * 100).toFixed(2)}%
      `;
    }
  }
  
  /**
   * Set up scroll listener for cleanup
   */
  setupScrollListener() {
    // Throttled scroll handler
    const scrollHandler = this.throttle(() => {
      this.cleanupOffscreenImages();
    }, 500);
    
    // Add scroll event listeners
    window.addEventListener('scroll', scrollHandler);
    
    // Also handle scroll on product grid if it exists
    const productGrid = document.querySelector('#product-grid');
    if (productGrid) {
      productGrid.addEventListener('scroll', scrollHandler);
    }
  }
  
  /**
   * Clean up images for products that are far from the viewport
   */
  cleanupOffscreenImages() {
    if (!this.cleanupEnabled) return;
    
    const viewportTop = window.scrollY;
    const viewportBottom = viewportTop + window.innerHeight;
    const buffer = this.config.viewportBuffer;
    
    // Get all products with loaded images
    this.loadedProducts.forEach((status, productId) => {
      if (status.fullyLoaded) {
        // Find the product card
        const productCard = document.querySelector(`.card-wrapper .product-images-template[data-product-id="${productId}"]`)?.closest('.card-wrapper');
        
        if (productCard) {
          const rect = productCard.getBoundingClientRect();
          const productTop = rect.top + window.scrollY;
          const productBottom = rect.bottom + window.scrollY;
          
          // Check if product is far from viewport
          const isFarAbove = productBottom < viewportTop - buffer;
          const isFarBelow = productTop > viewportBottom + buffer;
          
          if ((isFarAbove || isFarBelow) && !this.cyclingProducts.has(productCard)) {
            this.cleanupProductImages(productCard, productId);
          }
        }
      }
    });
    
    // Update memory debugger
    if (this.memoryDebug) {
      this.updateMemoryDebugger();
    }
  }
  
  /**
   * Clean up images for a specific product
   * @param {HTMLElement} productCard - The product card element
   * @param {string} productId - The product ID
   */
  cleanupProductImages(productCard, productId) {
    // Don't clean up if product is currently cycling images
    if (this.cyclingProducts.has(productCard)) {
      return;
    }
    
    // Get all images except the first one (primary image)
    const images = Array.from(productCard.querySelectorAll('.product-desktop-image'));
    const primaryImage = images.find(img => img.classList.contains('image-active')) || images[0];
    
    // Remove all images except the primary one
    images.forEach(img => {
      if (img !== primaryImage) {
        img.remove();
      }
    });
    
    // Update loaded status
    if (this.loadedProducts.has(productId)) {
      const status = this.loadedProducts.get(productId);
      status.fullyLoaded = false;
      this.loadedProducts.set(productId, status);
    }
  }
  
  /**
   * Set up intersection observer for lazy loading
   */
  setupIntersectionObserver() {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const productCard = entry.target;
          
          const template = productCard.querySelector('.product-images-template');
          if (template) {
            const productId = template.dataset.productId;
            
            // Check if we've already preloaded this product
            const loadStatus = this.loadedProducts.get(productId);
            if (!loadStatus || !loadStatus.preloaded) {
              this.preloadFirstImage(productCard);
            }
          }
          
          this.observer.unobserve(productCard);
        }
      });
    }, { rootMargin: '100px' });
    
    // Observe all product cards
    const productCards = document.querySelectorAll('.card-wrapper');
    
    productCards.forEach(card => {
      this.observer.observe(card);
    });
  }
  
  /**
   * Initialize visible products
   */
  initVisibleProducts() {
    document.querySelectorAll('.card-wrapper:not(.initialized)').forEach(card => {
      // Mark the first image as active
      const firstImage = card.querySelector('.product-desktop-image');
      if (firstImage) {
        firstImage.classList.add('image-active');
      }
      
      card.classList.add('initialized');
    });
  }
  
  /**
   * Preload the first image from the template
   * @param {HTMLElement} productCard - The product card element
   */
  preloadFirstImage(productCard) {
    const template = productCard.querySelector('.product-images-template');
    if (!template) {
      return;
    }
    
    const productId = template.dataset.productId;
    if (this.loadedProducts.has(productId) && this.loadedProducts.get(productId).preloaded) {
      return;
    }
    
    // Get the first image from the template
    const firstTemplateImage = template.content.querySelector('img');
    if (firstTemplateImage) {
      // Create a new image element
      const newImage = firstTemplateImage.cloneNode(true);
      newImage.style.display = 'none';
      
      // Add it to the DOM
      const mediaContainer = productCard.querySelector('.media');
      if (mediaContainer) {
        mediaContainer.appendChild(newImage);
        
        // Track that we've preloaded this product
        if (!this.loadedProducts.has(productId)) {
          this.loadedProducts.set(productId, { preloaded: true, fullyLoaded: false });
        } else {
          const status = this.loadedProducts.get(productId);
          status.preloaded = true;
          this.loadedProducts.set(productId, status);
        }
      }
    }
  }
  
  /**
   * Load all images from template for a product
   * @param {HTMLElement} productCard - The product card element
   * @returns {Promise} - Resolves when images are loaded
   */
  loadAllImagesFromTemplate(productCard) {
    return new Promise(resolve => {
      const template = productCard.querySelector('.product-images-template');
      if (!template) {
        resolve();
        return;
      }
      
      const productId = template.dataset.productId;
      
      // Check if we've already fully loaded images from this template
      if (this.loadedProducts.has(productId) && this.loadedProducts.get(productId).fullyLoaded) {
        resolve();
        return;
      }
      
      // Get all images from the template
      const templateContent = template.content;
      
      const templateImages = templateContent.querySelectorAll('img');
      
      if (templateImages.length === 0) {
        resolve();
        return;
      }
      
      const mediaContainer = productCard.querySelector('.media');
      if (!mediaContainer) {
        resolve();
        return;
      }
      
      // Get existing images to avoid duplicates
      const existingImageIds = new Set();
      mediaContainer.querySelectorAll('.product-desktop-image').forEach(img => {
        const imageId = img.dataset.imageId;
        if (imageId) {
          existingImageIds.add(imageId);
        }
      });
      
      // Clone and append each image that doesn't already exist
      let newImagesAdded = 0;
      templateImages.forEach((img, index) => {
        const imageId = img.dataset.imageId;
        
        // Skip if this image already exists
        if (imageId && existingImageIds.has(imageId)) {
          return;
        }
        
        const newImage = img.cloneNode(true);
        newImage.style.display = 'none';
        mediaContainer.appendChild(newImage);
        newImagesAdded++;
        
        if (imageId) {
          existingImageIds.add(imageId);
        }
      });
      
      // Mark as fully loaded
      if (!this.loadedProducts.has(productId)) {
        this.loadedProducts.set(productId, { preloaded: true, fullyLoaded: true });
      } else {
        const status = this.loadedProducts.get(productId);
        status.fullyLoaded = true;
        this.loadedProducts.set(productId, status);
      }
      
      resolve();
    });
  }
  
  /**
   * Ensure an image is loaded
   * @param {HTMLImageElement} img - The image element
   * @returns {Promise} - Resolves when the image is loaded
   */
  ensureImageLoaded(img) {
    return new Promise((resolve) => {
      if (img.complete) {
        img.classList.add('image-is-loaded');
        resolve();
      } else {
        img.setAttribute('loading', 'eager');
        
        const loadHandler = () => {
          img.classList.add('image-is-loaded');
          img.removeEventListener('load', loadHandler);
          resolve();
        };
        
        img.addEventListener('load', loadHandler);
        
        // Fallback timeout
        setTimeout(() => {
          if (!img.classList.contains('image-is-loaded')) {
            img.removeEventListener('load', loadHandler);
            resolve();
          }
        }, this.config.loadTimeout);
      }
    });
  }
  
  /**
   * Start cycling images for a product card
   * @param {HTMLElement} productCard - The product card element
   */
  startCycle(productCard) {
    // First load all images if needed
    this.loadAllImagesFromTemplate(productCard).then(() => {
      // Get all images (both initial and from template)
      const images = Array.from(productCard.querySelectorAll('.product-desktop-image'));
      
      if (images.length <= 1) return;
      
      let currentIndex = 0;
      productCard.classList.add('hovering-card');
      
      // Find the currently active image
      const activeImageIndex = images.findIndex(img => img.classList.contains('image-active'));
      if (activeImageIndex !== -1) {
        currentIndex = activeImageIndex;
      }
      
      // Function to cycle to the next image
      const cycleNextImage = () => {
        currentIndex = (currentIndex + 1) % images.length;
        
        // Ensure the next image is loaded before showing it
        this.ensureImageLoaded(images[currentIndex]).then(() => {
          // Remove active class from all images
          images.forEach(img => {
            img.classList.remove('image-active');
            img.style.display = 'none'; // Hide all images
          });
          
          // Show the next image
          images[currentIndex].style.display = '';
          images[currentIndex].classList.add('image-active');
          
          // Schedule the next cycle
          const timerId = setTimeout(cycleNextImage, this.config.cycleInterval);
          this.cyclingProducts.set(productCard, timerId);
        });
      };
      
      // Start cycling after delay
      const initialTimerId = setTimeout(cycleNextImage, this.config.cycleDelay);
      this.cyclingProducts.set(productCard, initialTimerId);
      
      // Update memory debugger
      if (this.memoryDebug) {
        this.updateMemoryDebugger();
      }
    });
  }
  
  /**
   * Stop cycling images for a product card
   * @param {HTMLElement} productCard - The product card element
   */
  stopCycle(productCard) {
    // Clear any existing timers
    if (this.cyclingProducts.has(productCard)) {
      clearTimeout(this.cyclingProducts.get(productCard));
      this.cyclingProducts.delete(productCard);
    }
    
    // Reset to first image
    const images = productCard.querySelectorAll('.product-desktop-image');
    images.forEach((img, index) => {
      if (index === 0) {
        img.classList.add('image-active');
        img.style.display = '';
      } else {
        img.classList.remove('image-active');
        img.style.display = 'none';
      }
    });
    
    productCard.classList.remove('hovering-card');
    
    // If cleanup is enabled, schedule cleanup after delay
    if (this.cleanupEnabled) {
      const template = productCard.querySelector('.product-images-template');
      if (template) {
        const productId = template.dataset.productId;
        
        setTimeout(() => {
          // Only clean up if not cycling again
          if (!this.cyclingProducts.has(productCard)) {
            this.cleanupProductImages(productCard, productId);
          }
        }, this.config.cleanupDelay);
      }
    }
    
    // Update memory debugger
    if (this.memoryDebug) {
      this.updateMemoryDebugger();
    }
  }
  
  /**
   * Stop all cycling images
   */
  stopAllCycles() {
    this.cyclingProducts.forEach((timerId, productCard) => {
      clearTimeout(timerId);
      productCard.classList.remove('hovering-card');
      
      // Reset to first image
      const images = productCard.querySelectorAll('.product-desktop-image');
      images.forEach((img, index) => {
        if (index === 0) {
          img.classList.add('image-active');
          img.style.display = '';
        } else {
          img.classList.remove('image-active');
          img.style.display = 'none';
        }
      });
    });
    
    this.cyclingProducts.clear();
    
    // If cleanup is enabled, schedule cleanup for all products
    if (this.cleanupEnabled) {
      setTimeout(() => {
        this.cleanupOffscreenImages();
      }, this.config.cleanupDelay);
    }
    
    // Update memory debugger
    if (this.memoryDebug) {
      this.updateMemoryDebugger();
    }
  }
  
  /**
   * Bind events for desktop
   */
  bindDesktopEvents() {
    // Handle hover events on product cards
    document.addEventListener('mouseover', (event) => {
      const productCard = event.target.closest('.card-wrapper:not(.plp-auto-hover):not(.view-all-product)');
      if (productCard && !productCard.classList.contains('hovering-card')) {
        this.startCycle(productCard);
      }
    }, false);
    
    document.addEventListener('mouseout', (event) => {
      const productCard = event.target.closest('.card-wrapper');
      const relatedTarget = event.relatedTarget;
      
      if (productCard && !productCard.contains(relatedTarget)) {
        this.stopCycle(productCard);
      }
    }, false);
    
    // Handle scroll events
    window.addEventListener('scroll', this.debounce(() => {
      this.stopAllCycles();
    }, this.config.debounceDelay));
  }
  
  /**
   * Bind events for mobile
   */
  bindMobileEvents() {
    // Handle add to bag button clicks
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('.mobile-atb-trigger, .plp--atb-trigger:not(.js-product-add-to-cart-plp):not(.plp-final-sale-options-trigger):not(.item-in-cart)');
      if (trigger) {
        this.stopAllCycles();
        
        const productCard = trigger.closest('.card-wrapper');
        if (productCard) {
          this.startCycle(productCard);
          productCard.classList.add('card-showing-atb');
        }
      }
    }, false);
    
    // Handle scroll events
    window.addEventListener('scroll', this.debounce(() => {
      this.stopAllCycles();
    }, this.config.debounceDelay));
  }
  
  /**
   * Handle window resize
   */
  handleResize() {
    window.addEventListener('resize', this.debounce(() => {
      const wasMobile = this.isMobile;
      this.isMobile = window.matchMedia('(max-width: 749px)').matches;
      
      // If device type changed, rebind events
      if (wasMobile !== this.isMobile) {
        this.stopAllCycles();
        
        if (this.isMobile) {
          this.bindMobileEvents();
        } else {
          this.bindDesktopEvents();
        }
      }
    }, 250));
  }
  
  /**
   * Debounce function
   * @param {Function} fn - Function to debounce
   * @param {number} delay - Delay in milliseconds
   * @returns {Function} - Debounced function
   */
  debounce(fn, delay) {
    let timer;
    return function() {
      const context = this;
      const args = arguments;
      clearTimeout(timer);
      timer = setTimeout(() => {
        fn.apply(context, args);
      }, delay);
    };
  }
  
  /**
   * Throttle function
   * @param {Function} fn - Function to throttle
   * @param {number} limit - Throttle limit in milliseconds
   * @returns {Function} - Throttled function
   */
  throttle(fn, limit) {
    let inThrottle;
    return function() {
      const context = this;
      const args = arguments;
      if (!inThrottle) {
        fn.apply(context, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  }
  
  /**
   * Toggle memory debugger
   */
  toggleMemoryDebugger() {
    this.memoryDebug = !this.memoryDebug;
    
    // Check if our mobile debugger exists
    const mobileDebugger = document.getElementById('mobile-memory-debugger');
    if (mobileDebugger) {
      // Use the mobile debugger instead
      mobileDebugger.style.display = this.memoryDebug ? 'block' : 'none';
      
      // Update the toggle button
      const toggleBtn = document.getElementById('memory-debugger-toggle');
      if (toggleBtn) {
        toggleBtn.classList.toggle('active', this.memoryDebug);
        toggleBtn.textContent = this.memoryDebug ? 'Memory Debug: On' : 'Memory Debug: Off';
      }
    } else {
      // Use the built-in debugger
      if (this.memoryDebug) {
        this.initMemoryDebugger();
      } else {
        clearInterval(this.memoryUpdateInterval);
        const debuggerEl = document.getElementById('memory-debugger');
        if (debuggerEl) {
          debuggerEl.remove();
        }
      }
    }
  }
  
  /**
   * Toggle cleanup
   */
  toggleCleanup() {
    this.cleanupEnabled = !this.cleanupEnabled;
    
    if (this.cleanupEnabled) {
      this.setupScrollListener();
      this.cleanupOffscreenImages();
    }
  }
  
  /**
   * Reinitialize for new content
   */
  reinit() {
    // Initialize visible products
    this.initVisibleProducts();
    
    // Find new product cards that need to be observed
    const productCards = document.querySelectorAll('.card-wrapper:not(.initialized)');
    
    // Observe new product cards
    productCards.forEach(card => {
      this.observer.observe(card);
      
      // Check if this card has a template
      const template = card.querySelector('.product-images-template');
      if (template) {
        const productId = template.dataset.productId;
        
        // If we haven't preloaded this product yet, do it now
        if (!this.loadedProducts.has(productId)) {
          this.preloadFirstImage(card);
        }
      }
    });
    
    // Update memory debugger
    if (this.memoryDebug) {
      this.updateMemoryDebugger();
    }
  }
}

// Initialize on DOMContentLoaded
document.addEventListener('DOMContentLoaded', () => {
  window.productImageLoader = new ProductImageLoader();
  
  // Add keyboard shortcut to toggle memory debugger (Alt+Shift+D)
  document.addEventListener('keydown', (event) => {
    if (event.altKey && event.shiftKey && event.key === 'D') {
      window.productImageLoader.toggleMemoryDebugger();
    }
  });
  
  // Add keyboard shortcut to toggle cleanup (Alt+Shift+C)
  document.addEventListener('keydown', (event) => {
    if (event.altKey && event.shiftKey && event.key === 'C') {
      window.productImageLoader.toggleCleanup();
    }
  });
});

// For backward compatibility with existing code
function autoImageHover(productHoverHandle) {
  // This function is kept for backward compatibility
  // It will be called by existing code but won't do anything
  // as the new ProductImageLoader handles everything
} 